"use client";

import { c as _c } from "react/compiler-runtime";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import { useWave } from "@/hooks/useWave";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, useState, type MouseEvent } from "react";
import { Tooltip } from "react-tooltip";
type ResubmitActionVariant = "icon" | "menu" | "button";
interface MemesArtResubmitActionProps {
  readonly drop: ExtendedDrop;
  readonly wave?: ApiWave | null | undefined;
  readonly variant?: ResubmitActionVariant | undefined;
  readonly onOpenModal?: (() => void) | undefined;
  readonly onModalClose?: (() => void) | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
interface MemesArtResubmitActionWithWaveProps {
  readonly drop: ExtendedDrop;
  readonly wave: ApiWave;
  readonly variant: ResubmitActionVariant;
  readonly onOpenModal?: (() => void) | undefined;
  readonly onModalClose?: (() => void) | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}
const getDisabledReason = ({
  canSubmitNow,
  hasReachedLimit,
  isEligible,
  isWithinPeriod
}: {
  readonly canSubmitNow: boolean;
  readonly hasReachedLimit: boolean;
  readonly isEligible: boolean;
  readonly isWithinPeriod: boolean;
}): string | null => {
  if (canSubmitNow) {
    return null;
  }
  if (hasReachedLimit) {
    return "Resubmit creates a new submission before deleting the original, so it is unavailable after reaching the submission limit.";
  }
  if (!isWithinPeriod) {
    return "Submissions are not currently open.";
  }
  if (!isEligible) {
    return "Your profile is not eligible to submit to this wave.";
  }
  return "Resubmit is not available right now.";
};
export function MemesArtResubmitAction(t0) {
  const $ = _c(7);
  const {
    drop,
    wave,
    variant: t1,
    onOpenModal,
    onModalClose,
    onSourceDropDeleted
  } = t0;
  const variant = t1 === undefined ? "icon" : t1;
  if (!wave) {
    return null;
  }
  let t2;
  if ($[0] !== drop || $[1] !== onModalClose || $[2] !== onOpenModal || $[3] !== onSourceDropDeleted || $[4] !== variant || $[5] !== wave) {
    t2 = <MemesArtResubmitActionWithWave drop={drop} wave={wave} variant={variant} onOpenModal={onOpenModal} onModalClose={onModalClose} onSourceDropDeleted={onSourceDropDeleted} />;
    $[0] = drop;
    $[1] = onModalClose;
    $[2] = onOpenModal;
    $[3] = onSourceDropDeleted;
    $[4] = variant;
    $[5] = wave;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function MemesArtResubmitActionWithWave(t0) {
  const $ = _c(63);
  const {
    drop,
    wave,
    variant,
    onOpenModal,
    onModalClose,
    onSourceDropDeleted
  } = t0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    canDelete,
    isAuthor,
    isWinner
  } = useDropInteractionRules(drop);
  const {
    participation
  } = useWave(wave);
  const isPotentialResubmission = isAuthor && canDelete && !isWinner;
  let t1;
  if ($[0] !== participation.canSubmitNow || $[1] !== participation.hasReachedLimit || $[2] !== participation.isEligible || $[3] !== participation.isWithinPeriod) {
    t1 = getDisabledReason({
      canSubmitNow: participation.canSubmitNow,
      hasReachedLimit: participation.hasReachedLimit,
      isEligible: participation.isEligible,
      isWithinPeriod: participation.isWithinPeriod
    });
    $[0] = participation.canSubmitNow;
    $[1] = participation.hasReachedLimit;
    $[2] = participation.isEligible;
    $[3] = participation.isWithinPeriod;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const disabledReason = t1;
  const disabled = drop.drop_type !== ApiDropType.Participatory || disabledReason !== null;
  let t2;
  if ($[5] !== disabled || $[6] !== onOpenModal) {
    t2 = event => {
      event.stopPropagation();
      if (disabled) {
        return;
      }
      if (onOpenModal) {
        onOpenModal();
        return;
      }
      setIsModalOpen(true);
    };
    $[5] = disabled;
    $[6] = onOpenModal;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const openModal = t2;
  let t3;
  if ($[8] !== onModalClose) {
    t3 = () => {
      setIsModalOpen(false);
      onModalClose?.();
    };
    $[8] = onModalClose;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const closeModal = t3;
  if (!isPotentialResubmission) {
    return null;
  }
  const title = disabledReason ?? "Resubmit";
  const tooltipId = `resubmit-${drop.id}-${variant}`;
  let t4;
  if ($[10] !== closeModal || $[11] !== drop || $[12] !== isModalOpen || $[13] !== onSourceDropDeleted || $[14] !== wave) {
    t4 = <MemesArtSubmissionModal isOpen={isModalOpen} wave={wave} sourceDrop={drop} onClose={closeModal} onSourceDropDeleted={onSourceDropDeleted} />;
    $[10] = closeModal;
    $[11] = drop;
    $[12] = isModalOpen;
    $[13] = onSourceDropDeleted;
    $[14] = wave;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const modal = t4;
  if (variant === "menu") {
    const t5 = `tw-flex tw-w-full tw-items-center tw-gap-x-4 tw-rounded-xl tw-border-0 tw-bg-iron-950 tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 ${disabled ? "tw-cursor-not-allowed tw-text-iron-600" : "tw-text-iron-200 active:tw-bg-iron-800"}`;
    let t6;
    let t7;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ArrowPathIcon className="tw-size-5 tw-flex-shrink-0" />;
      t7 = <span className="tw-text-base tw-font-semibold">Resubmit</span>;
      $[16] = t6;
      $[17] = t7;
    } else {
      t6 = $[16];
      t7 = $[17];
    }
    let t8;
    if ($[18] !== disabled || $[19] !== openModal || $[20] !== t5 || $[21] !== title) {
      t8 = <button type="button" onClick={openModal} aria-disabled={disabled} title={title} className={t5}>{t6}{t7}</button>;
      $[18] = disabled;
      $[19] = openModal;
      $[20] = t5;
      $[21] = title;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== disabledReason) {
      t9 = disabledReason && <p className="tw-mb-0 tw-mt-2 tw-px-4 tw-text-xs tw-leading-5 tw-text-iron-500">{disabledReason}</p>;
      $[23] = disabledReason;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t8 || $[26] !== t9) {
      t10 = <div>{t8}{t9}</div>;
      $[25] = t8;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== modal || $[29] !== t10) {
      t11 = <>{t10}{modal}</>;
      $[28] = modal;
      $[29] = t10;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    return t11;
  }
  if (variant === "button") {
    const t5 = `tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-3 tw-text-xs tw-transition-all tw-duration-300 tw-ease-out ${disabled ? "tw-cursor-not-allowed tw-border-iron-800 tw-bg-iron-900 tw-text-iron-600" : "tw-text-primary-200 tw-border-primary-500/30 tw-bg-primary-500/10 desktop-hover:hover:tw-border-primary-400/40 desktop-hover:hover:tw-bg-primary-500/20"}`;
    let t6;
    let t7;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <ArrowPathIcon className="tw-size-4 tw-flex-shrink-0" />;
      t7 = <span>Resubmit Drop</span>;
      $[31] = t6;
      $[32] = t7;
    } else {
      t6 = $[31];
      t7 = $[32];
    }
    let t8;
    if ($[33] !== disabled || $[34] !== openModal || $[35] !== t5 || $[36] !== title) {
      t8 = <button type="button" onClick={openModal} aria-disabled={disabled} title={title} className={t5}>{t6}{t7}</button>;
      $[33] = disabled;
      $[34] = openModal;
      $[35] = t5;
      $[36] = title;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    let t9;
    if ($[38] !== disabledReason) {
      t9 = disabledReason && <p className="tw-mb-0 tw-max-w-xs tw-text-xs tw-leading-5 tw-text-iron-500">{disabledReason}</p>;
      $[38] = disabledReason;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    let t10;
    if ($[40] !== t8 || $[41] !== t9) {
      t10 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t8}{t9}</div>;
      $[40] = t8;
      $[41] = t9;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    let t11;
    if ($[43] !== modal || $[44] !== t10) {
      t11 = <>{t10}{modal}</>;
      $[43] = modal;
      $[44] = t10;
      $[45] = t11;
    } else {
      t11 = $[45];
    }
    return t11;
  }
  const t5 = `tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-2 tw-transition-colors ${disabled ? "tw-cursor-not-allowed tw-text-iron-600" : "tw-text-iron-400 desktop-hover:hover:tw-text-primary-300"}`;
  let t6;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowPathIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== disabled || $[48] !== openModal || $[49] !== t5 || $[50] !== title || $[51] !== tooltipId) {
    t7 = <button type="button" onClick={openModal} aria-disabled={disabled} title={title} className={t5} aria-label="Resubmit drop" data-tooltip-id={tooltipId}>{t6}</button>;
    $[47] = disabled;
    $[48] = openModal;
    $[49] = t5;
    $[50] = title;
    $[51] = tooltipId;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px",
      zIndex: 10000,
      pointerEvents: "none",
      maxWidth: "280px"
    };
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  let t9;
  if ($[54] !== title) {
    t9 = <span className="tw-text-xs">{title}</span>;
    $[54] = title;
    $[55] = t9;
  } else {
    t9 = $[55];
  }
  let t10;
  if ($[56] !== t9 || $[57] !== tooltipId) {
    t10 = <Tooltip id={tooltipId} place="top-end" offset={8} opacity={1} positionStrategy="fixed" style={t8}>{t9}</Tooltip>;
    $[56] = t9;
    $[57] = tooltipId;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] !== modal || $[60] !== t10 || $[61] !== t7) {
    t11 = <>{t7}{t10}{modal}</>;
    $[59] = modal;
    $[60] = t10;
    $[61] = t7;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  return t11;
}